import { useMemo } from 'react';
import { compareVersionItems, getVersionChangeSummary } from '../utils/versionCompare';
import { logger } from '../utils/logger';

/**
 * Custom hook for version comparison logic
 * @param {Array} versions - Array of version objects
 * @param {string} type - Type of data ('cv', 'job', or 'company')
 * @returns {Array} Processed versions with comparison data
 */
export const useVersionComparison = (versions, type) => {
  const processedVersions = useMemo(() => {
    if (!versions || versions.length === 0) {
      logger.warn('[useVersionComparison]', 'No versions provided', { versions, type });
      return [];
    }

    logger.info('[useVersionComparison]', 'Processing versions for comparison', {
      versionCount: versions.length,
      type,
      firstVersionKeys: versions[0] ? Object.keys(versions[0]) : [],
      firstVersionStructure: versions[0] ? {
        hasData: !!versions[0].data,
        hasResult: !!versions[0].data?.result,
        hasCompetencies: !!versions[0].data?.result?.competencies,
        competencyCount: versions[0].data?.result?.competencies?.length || 0
      } : null
    });

    // Sort versions by timestamp (newest first)
    const sorted = [...versions].sort((a, b) => {
      // Extract timestamps from version strings if needed
      let timestampA = a.timestamp;
      let timestampB = b.timestamp;

      // Handle version format (YYYY-MM-DD_HH-MM-SS)
      if (a.version && typeof a.version === 'string' && a.version.includes('_')) {
        const [datePart, timePart] = a.version.split('_');
        const [year, month, day] = datePart.split('-');
        const [hour, minute, second] = timePart.split('-');
        timestampA = new Date(year, month - 1, day, hour, minute, second).getTime();
      }

      if (b.version && typeof b.version === 'string' && b.version.includes('_')) {
        const [datePart, timePart] = b.version.split('_');
        const [year, month, day] = datePart.split('-');
        const [hour, minute, second] = timePart.split('-');
        timestampB = new Date(year, month - 1, day, hour, minute, second).getTime();
      }

      return timestampB - timestampA;
    });

    // Add comparison data for each version
    logger.info('[useVersionComparison]', 'Processing sorted versions', {
      sortedCount: sorted.length,
      sortedVersions: sorted.map(v => ({ version: v.version, hasData: !!v.data }))
    });

    return sorted.map((version, index) => {
      // For the first version (newest), there's no previous version to compare with
      if (index === sorted.length - 1) {
        return {
          ...version,
          comparisonData: {
            added: [],
            removed: [],
            modified: []
          },
          comparisonSummary: 'No previous version to compare'
        };
      }

      // For other versions, compare with the next one (which is the previous version chronologically)
      const previousVersion = sorted[index + 1];

      // Get the data arrays from each version
      let currentData = [];
      let previousData = [];

      if (type === 'cv') {
        currentData = version.data?.result?.competencies || [];
        previousData = previousVersion.data?.result?.competencies || [];
      } else if (type === 'job') {
        currentData = version.data?.result?.skills || [];
        previousData = previousVersion.data?.result?.skills || [];
      } else if (type === 'company') {
        // For company research, we compare sections
        currentData = version.data?.result?.sections || [];
        previousData = previousVersion.data?.result?.sections || [];
      }

      // Enhanced debugging for comparison data
      logger.info('[useVersionComparison]', `Comparing versions ${version.version} vs ${previousVersion.version}`, {
        currentDataLength: currentData.length,
        previousDataLength: previousData.length,
        currentDataSample: currentData.slice(0, 2).map(item => ({
          key: type === 'cv' ? item.competency : item.name || item.skill,
          proficiency: item.proficiency,
          category: item.category
        })),
        previousDataSample: previousData.slice(0, 2).map(item => ({
          key: type === 'cv' ? item.competency : item.name || item.skill,
          proficiency: item.proficiency,
          category: item.category
        }))
      });

      const comparisonData = compareVersionItems(currentData, previousData, type);

      logger.info('[useVersionComparison]', `Comparison result for ${version.version}`, {
        added: comparisonData.added?.length || 0,
        removed: comparisonData.removed?.length || 0,
        modified: comparisonData.modified?.length || 0,
        hasChanges: (comparisonData.added?.length || 0) +
          (comparisonData.removed?.length || 0) +
          (comparisonData.modified?.length || 0) > 0
      });

      // Ensure all required arrays exist
      const safeComparisonData = {
        added: comparisonData.added || [],
        removed: comparisonData.removed || [],
        modified: comparisonData.modified || []
      };

      // Create version-like objects for the summary function with correct structure
      let currentVersionForSummary, previousVersionForSummary;
      if (type === 'cv') {
        currentVersionForSummary = { competencies: currentData };
        previousVersionForSummary = { competencies: previousData };
      } else if (type === 'job') {
        currentVersionForSummary = { skills: currentData };
        previousVersionForSummary = { skills: previousData };
      } else if (type === 'company') {
        currentVersionForSummary = { sections: currentData };
        previousVersionForSummary = { sections: previousData };
      }

      const summary = getVersionChangeSummary(currentVersionForSummary, previousVersionForSummary, type);

      return {
        ...version,
        comparisonData: safeComparisonData,
        comparisonSummary: summary,
        previousVersionId: previousVersion.version
      };
    });
  }, [versions, type]);

  logger.info('[useVersionComparison]', 'Final processed versions', {
    inputVersionCount: versions?.length || 0,
    outputVersionCount: processedVersions.length,
    hasProcessedVersions: processedVersions.length > 0
  });

  return processedVersions;
};

/**
 * Get author label for display
 * @param {string} author - Author string
 * @returns {string} Formatted author label
 */
export const getAuthorLabel = (author) => {
  if (!author) return 'System';

  // Handle special cases
  if (author === 'USER') return 'User';
  if (author === 'ADMIN') return 'Admin';
  if (author === 'SYSTEM') return 'System';
  if (author === 'API') return 'API Import';

  // For email addresses, show only the username part
  if (author.includes('@')) {
    return author.split('@')[0];
  }

  // For full names with spaces
  if (author.includes(' ')) {
    const parts = author.split(' ');
    if (parts.length === 2) {
      // Show first name and last initial
      return `${parts[0]} ${parts[1].charAt(0)}.`;
    }
  }

  return author;
};

/**
 * Format date for display
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';

  try {
    let date;

    // Check if it's a version string format (YYYY-MM-DD_HH-MM-SS)
    if (dateString.includes('_') && dateString.includes('-')) {
      const [datePart, timePart] = dateString.split('_');
      const [year, month, day] = datePart.split('-');
      const [hour, minute, second] = timePart.split('-');
      date = new Date(year, month - 1, day, hour, minute, second || 0);
    } else {
      // Try parsing as regular date
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      logger.warn('[formatDate]', 'Invalid date string:', dateString);
      return dateString;
    }

    // Format as readable date and time
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };

    return date.toLocaleString(undefined, options);
  } catch (error) {
    logger.error('[formatDate]', 'Error formatting date:', error);
    return dateString;
  }
};

/**
 * Get the item key based on type
 * @param {string} type - Data type ('cv', 'job', or 'company')
 * @returns {string} The key name for items
 */
export const getItemKey = (type) => {
  switch (type) {
    case 'cv':
      return 'competencies';
    case 'job':
      return 'skills';
    case 'company':
      return 'sections';
    default:
      return 'items';
  }
};
